import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UTTOnboardingWizard } from '../components/utt/UTTOnboardingWizard';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  TrendingUp, 
  TrendingDown,
  PieChart,
  BarChart3,
  ShieldCheck,
  Clock,
  Users,
  Building2,
  Phone,
  Globe,
  Mail,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Info,
  Calculator,
  Wallet,
  Target,
  Baby,
  Landmark,
  Banknote,
  Heart,
  Scale,
  Loader2,
  ExternalLink,
  CheckCircle2
} from 'lucide-react';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fundIcons = {
  UMOJA: Scale,
  WATOTO: Baby,
  JIKIMU: Banknote,
  WEKEZA_MAISHA: Heart,
  LIQUID: Wallet,
  BOND: Landmark
};

const riskColors = {
  'Low': 'bg-green-100 text-green-700',
  'Low-Moderate': 'bg-blue-100 text-blue-700',
  'Moderate': 'bg-yellow-100 text-yellow-700',
  'Moderate-High': 'bg-orange-100 text-orange-700',
  'High': 'bg-red-100 text-red-700'
};

export default function UTTAmis() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [funds, setFunds] = useState([]);
  const [companyInfo, setCompanyInfo] = useState(null);
  const [selectedFund, setSelectedFund] = useState(null);
  const [fundDetail, setFundDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  
  // Comparison state
  const [comparisonFunds, setComparisonFunds] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  
  // Simulator state
  const [simulatorFund, setSimulatorFund] = useState('');
  const [simulatorData, setSimulatorData] = useState({
    initial_amount: 500000,
    monthly_contribution: 50000,
    years: 5
  });
  const [simulationResult, setSimulationResult] = useState(null);
  const [simulating, setSimulating] = useState(false);

  const fetchFunds = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/utt-amis/funds`);
      setFunds(response.data.funds);
      setCompanyInfo(response.data.company);
    } catch (error) {
      toast.error(t('Failed to load funds', 'Imeshindwa kupakia mifuko'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchFunds();
  }, [fetchFunds]);

  const fetchFundDetail = async (fundId) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`${API}/utt-amis/funds/${fundId}`);
      setFundDetail(response.data);
      setIsDetailOpen(true);
    } catch (error) {
      toast.error(t('Failed to load fund details', 'Imeshindwa kupakia maelezo ya mfuko'));
    } finally {
      setDetailLoading(false);
    }
  };

  const handleFundClick = (fund) => {
    setSelectedFund(fund);
    fetchFundDetail(fund.id);
  };

  const handleCompare = async () => {
    if (comparisonFunds.length < 2) {
      toast.error(t('Select at least 2 funds to compare', 'Chagua angalau mifuko 2 kulinganisha'));
      return;
    }
    
    try {
      const response = await axios.get(`${API}/utt-amis/compare?fund_ids=${comparisonFunds.join(',')}`);
      setComparisonData(response.data.comparison);
    } catch (error) {
      toast.error(t('Failed to compare funds', 'Imeshindwa kulinganisha mifuko'));
    }
  };

  const toggleComparisonFund = (fundId) => {
    setComparisonFunds(prev => {
      if (prev.includes(fundId)) {
        return prev.filter(f => f !== fundId);
      }
      if (prev.length >= 4) {
        toast.info(t('Maximum 4 funds can be compared', 'Mifuko 4 tu inaweza kulinganishwa'));
        return prev;
      }
      return [...prev, fundId];
    });
  };

  const runSimulation = async () => {
    if (!simulatorFund) {
      toast.error(t('Please select a fund', 'Tafadhali chagua mfuko'));
      return;
    }
    
    setSimulating(true);
    try {
      const response = await axios.post(`${API}/utt-amis/simulate`, null, {
        params: {
          fund_id: simulatorFund,
          initial_amount: simulatorData.initial_amount,
          monthly_contribution: simulatorData.monthly_contribution,
          years: simulatorData.years
        }
      });
      setSimulationResult(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Simulation failed', 'Uigaji umeshindwa'));
    } finally {
      setSimulating(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 1000000000000) {
      return `${(amount / 1000000000000).toFixed(2)}T`;
    } else if (amount >= 1000000000) {
      return `${(amount / 1000000000).toFixed(2)}B`;
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount?.toLocaleString() || '0';
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-enter space-y-6">
        {/* Hero Section */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 p-6 sm:p-8 text-white">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLW9wYWNpdHk9IjAuMDUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-30" />
          <div className="relative">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <PieChart className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold">UTT AMIS</h1>
                <p className="text-white/80 text-sm sm:text-base">
                  {t('Unit Trust of Tanzania', 'Amana ya Uwekezaji wa Tanzania')}
                </p>
              </div>
            </div>
            <p className="text-white/90 max-w-2xl text-sm sm:text-base mb-6">
              {t(
                "Tanzania's premier asset management company offering professionally managed investment funds. Start investing from TZS 8,340 a month.",
                "Kampuni kuu ya usimamizi wa mali Tanzania inayotoa mifuko ya uwekezaji inayosimamiwa kitaalamu. Anza kuwekeza kuanzia TZS 8,340 kwa mwezi."
              )}
            </p>
            
            {companyInfo && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                  <p className="text-white/70 text-xs">{t('Total AUM', 'Jumla ya AUM')}</p>
                  <p className="text-xl font-bold">TZS {formatCurrency(companyInfo.total_aum)}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                  <p className="text-white/70 text-xs">{t('Investors', 'Wawekezaji')}</p>
                  <p className="text-xl font-bold">{formatNumber(companyInfo.total_investors)}+</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                  <p className="text-white/70 text-xs">{t('Avg Return', 'Faida Wastani')}</p>
                  <p className="text-xl font-bold text-green-300">~{companyInfo.average_return}%</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
                  <p className="text-white/70 text-xs">{t('Total Funds', 'Jumla Mifuko')}</p>
                  <p className="text-xl font-bold">{funds.length}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
            <TabsTrigger value="overview" data-testid="tab-overview">
              {t('Funds', 'Mifuko')}
            </TabsTrigger>
            <TabsTrigger value="compare" data-testid="tab-compare">
              {t('Compare', 'Linganisha')}
            </TabsTrigger>
            <TabsTrigger value="simulator" data-testid="tab-simulator">
              {t('Simulator', 'Kihesabu')}
            </TabsTrigger>
            <TabsTrigger value="open-account" data-testid="tab-open-account">
              {t('Open Account', 'Fungua Akaunti')}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="open-account" className="mt-6">
            <UTTOnboardingWizard funds={funds} />
          </TabsContent>

          {/* Funds Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            {funds[0]?.nav_as_of && (
              <p className="text-xs text-muted-foreground" data-testid="utt-nav-as-of">
                {t(`Prices (NAV per unit) as of ${funds[0].nav_as_of} · Source: UTT AMIS · updated automatically every business day`,
                   `Bei (Thamani Halisi ya Kipande) kufikia ${funds[0].nav_as_of} · Chanzo: UTT AMIS · husasishwa kila siku ya kazi`)}
              </p>
            )}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {funds.map((fund) => {
                const FundIcon = fundIcons[fund.id] || PieChart;
                return (
                  <Card 
                    key={fund.id}
                    className="border-border/50 card-hover cursor-pointer overflow-hidden"
                    onClick={() => handleFundClick(fund)}
                    data-testid={`fund-card-${fund.id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                            <FundIcon className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-base">
                              {language === 'sw' ? fund.name_sw : fund.name}
                            </CardTitle>
                            <p className="text-xs text-muted-foreground">
                              {language === 'sw' ? fund.fund_type_sw : fund.fund_type}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t('Unit Price', 'Bei ya Kipande')}
                          </span>
                          <span className="font-semibold text-foreground">
                            TZS {fund.sale_price.toLocaleString()}
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t('Annual Return', 'Faida ya Mwaka')}
                          </span>
                          <span className={`font-semibold flex items-center gap-1 ${
                            fund.annual_return >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {fund.annual_return >= 0 ? (
                              <ArrowUpRight className="w-4 h-4" />
                            ) : (
                              <ArrowDownRight className="w-4 h-4" />
                            )}
                            {fund.annual_return}%
                          </span>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-muted-foreground">
                            {t('Risk Level', 'Kiwango cha Hatari')}
                          </span>
                          <Badge className={riskColors[fund.risk_level] || 'bg-gray-100'}>
                            {language === 'sw' ? fund.risk_level_sw : fund.risk_level}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center justify-between pt-2 border-t border-border/50">
                          <span className="text-xs text-muted-foreground">
                            {t('Min Investment', 'Kiwango cha chini')}
                          </span>
                          <span className="text-sm font-medium text-foreground">
                            TZS {formatNumber(fund.min_investment)}
                          </span>
                        </div>
                        
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full mt-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFundClick(fund);
                          }}
                        >
                          {t('View Details', 'Tazama Maelezo')}
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            
            {/* Investment Channels */}
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-primary" />
                  {t('How to Invest', 'Jinsi ya Kuwekeza')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">
                        {t('Visit Offices', 'Tembelea Ofisi')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('UTT AMIS offices nationwide', 'Ofisi za UTT AMIS nchi nzima')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">USSD</p>
                      <p className="text-sm text-muted-foreground font-mono">*150*82#</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">
                        {t('Website', 'Tovuti')}
                      </p>
                      <a 
                        href="https://uttamis.co.tz" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1"
                      >
                        uttamis.co.tz
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Compare Tab */}
          <TabsContent value="compare" className="space-y-6 mt-6">
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle>{t('Select Funds to Compare', 'Chagua Mifuko Kulinganisha')}</CardTitle>
                <CardDescription>
                  {t('Choose 2-4 funds to compare their performance and features', 'Chagua mifuko 2-4 kulinganisha utendaji na sifa zao')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  {funds.map((fund) => (
                    <button
                      key={fund.id}
                      onClick={() => toggleComparisonFund(fund.id)}
                      className={`p-3 rounded-xl border-2 transition-all text-left ${
                        comparisonFunds.includes(fund.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      data-testid={`compare-select-${fund.id}`}
                    >
                      <div className="flex items-center gap-2">
                        {comparisonFunds.includes(fund.id) && (
                          <CheckCircle2 className="w-4 h-4 text-primary" />
                        )}
                        <span className="font-medium text-sm">{fund.name}</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {fund.annual_return}% {t('return', 'faida')}
                      </p>
                    </button>
                  ))}
                </div>
                
                <Button 
                  onClick={handleCompare}
                  disabled={comparisonFunds.length < 2}
                  className="w-full sm:w-auto"
                  data-testid="compare-btn"
                >
                  <Scale className="w-4 h-4 mr-2" />
                  {t('Compare Selected', 'Linganisha Zilizochaguliwa')} ({comparisonFunds.length})
                </Button>
              </CardContent>
            </Card>
            
            {/* Comparison Results */}
            {comparisonData && comparisonData.length > 0 && (
              <div className="space-y-6">
                {/* Performance Chart */}
                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle>{t('Performance Comparison', 'Ulinganisho wa Utendaji')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={comparisonData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <Tooltip 
                            contentStyle={{ 
                              background: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Legend />
                          <Bar 
                            dataKey="annual_return" 
                            fill="hsl(var(--primary))" 
                            name={t('Annual Return %', 'Faida ya Mwaka %')}
                            radius={[4, 4, 0, 0]}
                          />
                          <Bar 
                            dataKey="ytd_return" 
                            fill="hsl(var(--accent))" 
                            name={t('YTD Return %', 'Faida YTD %')}
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Comparison Table */}
                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle>{t('Detailed Comparison', 'Ulinganisho wa Kina')}</CardTitle>
                  </CardHeader>
                  <CardContent className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead>
                        <tr className="border-b border-border">
                          <th className="text-left py-3 text-sm font-medium text-muted-foreground">
                            {t('Feature', 'Sifa')}
                          </th>
                          {comparisonData.map(fund => (
                            <th key={fund.id} className="text-center py-3 text-sm font-medium text-foreground">
                              {fund.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-b border-border/50">
                          <td className="py-3 text-sm text-muted-foreground">
                            {t('Unit Price', 'Bei ya Kipande')}
                          </td>
                          {comparisonData.map(fund => (
                            <td key={fund.id} className="text-center py-3 font-medium">
                              TZS {fund.sale_price?.toLocaleString()}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-3 text-sm text-muted-foreground">
                            {t('Annual Return', 'Faida ya Mwaka')}
                          </td>
                          {comparisonData.map(fund => (
                            <td key={fund.id} className={`text-center py-3 font-medium ${
                              fund.annual_return >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {fund.annual_return}%
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-3 text-sm text-muted-foreground">
                            {t('3-Year Return', 'Faida ya Miaka 3')}
                          </td>
                          {comparisonData.map(fund => (
                            <td key={fund.id} className="text-center py-3 font-medium text-green-600">
                              {fund.three_year_return != null ? `${fund.three_year_return}%` : '—'}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-3 text-sm text-muted-foreground">
                            {t('Risk Level', 'Kiwango cha Hatari')}
                          </td>
                          {comparisonData.map(fund => (
                            <td key={fund.id} className="text-center py-3">
                              <Badge className={riskColors[fund.risk_level] || 'bg-gray-100'}>
                                {fund.risk_level}
                              </Badge>
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-3 text-sm text-muted-foreground">
                            {t('Min Investment', 'Uwekezaji wa Chini')}
                          </td>
                          {comparisonData.map(fund => (
                            <td key={fund.id} className="text-center py-3 font-medium">
                              TZS {fund.min_investment?.toLocaleString()}
                            </td>
                          ))}
                        </tr>
                        <tr className="border-b border-border/50">
                          <td className="py-3 text-sm text-muted-foreground">
                            {t('Exit Load', 'Ada ya Kutoka')}
                          </td>
                          {comparisonData.map(fund => (
                            <td key={fund.id} className="text-center py-3 font-medium">
                              {fund.exit_load}%
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="py-3 text-sm text-muted-foreground">
                            {t('Max Equity', 'Hisa ya Juu')}
                          </td>
                          {comparisonData.map(fund => (
                            <td key={fund.id} className="text-center py-3 font-medium">
                              {fund.max_equity_exposure}%
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
                
                {/* Price History Chart */}
                <Card className="border-border/50">
                  <CardHeader>
                    <CardTitle>{t('Price History (2025)', 'Historia ya Bei (2025)')}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: 'hsl(var(--muted-foreground))' }}
                            allowDuplicatedCategory={false}
                          />
                          <YAxis tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                          <Tooltip 
                            contentStyle={{ 
                              background: 'hsl(var(--card))', 
                              border: '1px solid hsl(var(--border))',
                              borderRadius: '8px'
                            }}
                          />
                          <Legend />
                          {comparisonData.map((fund, index) => {
                            const colors = ['hsl(var(--primary))', 'hsl(var(--accent))', '#22c55e', '#f59e0b'];
                            return (
                              <Line
                                key={fund.id}
                                data={fund.historical_prices}
                                dataKey="price"
                                name={fund.name}
                                stroke={colors[index % colors.length]}
                                strokeWidth={2}
                                dot={false}
                              />
                            );
                          })}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Simulator Tab */}
          <TabsContent value="simulator" className="space-y-6 mt-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Input Form */}
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-primary" />
                    {t('Investment Simulator', 'Kihesabu cha Uwekezaji')}
                  </CardTitle>
                  <CardDescription>
                    {t('Project your potential returns with UTT AMIS funds', 'Kadiria faida yako inayowezekana na mifuko ya UTT AMIS')}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Fund Selection */}
                  <div className="space-y-2">
                    <Label>{t('Select Fund', 'Chagua Mfuko')}</Label>
                    <Select 
                      value={simulatorFund} 
                      onValueChange={setSimulatorFund}
                    >
                      <SelectTrigger data-testid="simulator-fund-select">
                        <SelectValue placeholder={t('Choose a fund', 'Chagua mfuko')} />
                      </SelectTrigger>
                      <SelectContent>
                        {funds.map(fund => (
                          <SelectItem key={fund.id} value={fund.id}>
                            <div className="flex items-center justify-between w-full">
                              <span>{fund.name}</span>
                              <span className="text-xs text-muted-foreground ml-2">
                                {fund.annual_return}% {t('return', 'faida')}
                              </span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Initial Amount */}
                  <div className="space-y-2">
                    <Label>
                      {t('Initial Investment (TZS)', 'Uwekezaji wa Kwanza (TZS)')}
                    </Label>
                    <Input
                      type="number"
                      value={simulatorData.initial_amount}
                      onChange={(e) => setSimulatorData({
                        ...simulatorData,
                        initial_amount: parseInt(e.target.value) || 0
                      })}
                      className="h-12"
                      data-testid="simulator-initial"
                    />
                    <p className="text-xs text-muted-foreground">
                      TZS {simulatorData.initial_amount.toLocaleString()}
                    </p>
                  </div>
                  
                  {/* Monthly Contribution */}
                  <div className="space-y-2">
                    <Label>
                      {t('Monthly Contribution (TZS)', 'Mchango wa Kila Mwezi (TZS)')}
                    </Label>
                    <Input
                      type="number"
                      value={simulatorData.monthly_contribution}
                      onChange={(e) => setSimulatorData({
                        ...simulatorData,
                        monthly_contribution: parseInt(e.target.value) || 0
                      })}
                      className="h-12"
                      data-testid="simulator-monthly"
                    />
                    <p className="text-xs text-muted-foreground">
                      TZS {simulatorData.monthly_contribution.toLocaleString()} / {t('month', 'mwezi')}
                    </p>
                  </div>
                  
                  {/* Investment Period */}
                  <div className="space-y-4">
                    <Label>
                      {t('Investment Period', 'Kipindi cha Uwekezaji')}: {simulatorData.years} {t('years', 'miaka')}
                    </Label>
                    <Slider
                      value={[simulatorData.years]}
                      onValueChange={([val]) => setSimulatorData({
                        ...simulatorData,
                        years: val
                      })}
                      min={1}
                      max={30}
                      step={1}
                      className="w-full"
                      data-testid="simulator-years"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>1 {t('year', 'mwaka')}</span>
                      <span>30 {t('years', 'miaka')}</span>
                    </div>
                  </div>
                  
                  <Button 
                    onClick={runSimulation}
                    disabled={simulating || !simulatorFund}
                    className="w-full h-12"
                    data-testid="run-simulation-btn"
                  >
                    {simulating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('Calculating...', 'Inahesabu...')}
                      </>
                    ) : (
                      <>
                        <Calculator className="w-4 h-4 mr-2" />
                        {t('Calculate Returns', 'Hesabu Faida')}
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
              
              {/* Results */}
              <div className="space-y-6">
                {simulationResult ? (
                  <>
                    {/* Summary Card */}
                    <Card className="border-border/50 bg-gradient-to-br from-primary/5 to-accent/5">
                      <CardContent className="p-6">
                        <div className="text-center mb-6">
                          <p className="text-sm text-muted-foreground mb-1">
                            {t('Expected Value', 'Thamani Inayotarajiwa')}
                          </p>
                          <p className="text-4xl font-bold text-primary" data-testid="expected-value">
                            TZS {formatCurrency(simulationResult.projection.expected)}
                          </p>
                          <p className="text-sm text-green-600 mt-1">
                            +{simulationResult.projection.expected_return_percentage}% {t('total return', 'faida jumla')}
                          </p>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-xs text-muted-foreground">
                              {t('Conservative', 'Chini')}
                            </p>
                            <p className="font-semibold text-foreground">
                              TZS {formatCurrency(simulationResult.projection.conservative)}
                            </p>
                          </div>
                          <div className="border-x border-border">
                            <p className="text-xs text-muted-foreground">
                              {t('Invested', 'Ulioweka')}
                            </p>
                            <p className="font-semibold text-foreground">
                              TZS {formatCurrency(simulationResult.projection.total_contributions)}
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">
                              {t('Optimistic', 'Juu')}
                            </p>
                            <p className="font-semibold text-foreground">
                              TZS {formatCurrency(simulationResult.projection.optimistic)}
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Growth Chart */}
                    <Card className="border-border/50">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">
                          {t('Projected Growth', 'Ukuaji Unaotarajiwa')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={simulationResult.yearly_projection}>
                              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                              <XAxis 
                                dataKey="year" 
                                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                                tickFormatter={(v) => `Y${v}`}
                              />
                              <YAxis 
                                tick={{ fill: 'hsl(var(--muted-foreground))' }}
                                tickFormatter={(v) => formatCurrency(v)}
                              />
                              <Tooltip 
                                contentStyle={{ 
                                  background: 'hsl(var(--card))', 
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px'
                                }}
                                formatter={(value) => [`TZS ${value.toLocaleString()}`, '']}
                                labelFormatter={(label) => `${t('Year', 'Mwaka')} ${label}`}
                              />
                              <Line 
                                type="monotone"
                                dataKey="value" 
                                stroke="hsl(var(--primary))" 
                                strokeWidth={3}
                                name={t('Portfolio Value', 'Thamani ya Portfolio')}
                                dot={{ fill: 'hsl(var(--primary))' }}
                              />
                              <Line 
                                type="monotone"
                                dataKey="total_invested" 
                                stroke="hsl(var(--muted-foreground))" 
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                name={t('Total Invested', 'Jumla Iliyowekezwa')}
                                dot={false}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </CardContent>
                    </Card>
                    
                    {/* Disclaimer */}
                    <div className="p-4 bg-muted/50 rounded-lg text-xs text-muted-foreground">
                      <Info className="w-4 h-4 inline mr-2" />
                      {simulationResult.disclaimer}
                    </div>
                  </>
                ) : (
                  <Card className="border-dashed">
                    <CardContent className="p-12 text-center">
                      <Calculator className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {t('Run a Simulation', 'Fanya Uigaji')}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {t(
                          'Select a fund and enter your investment details to see projected returns',
                          'Chagua mfuko na ingiza maelezo ya uwekezaji wako kuona faida inayotarajiwa'
                        )}
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Fund Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {fundDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {(() => {
                    const FundIcon = fundIcons[fundDetail.fund.id] || PieChart;
                    return (
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                        <FundIcon className="w-6 h-6 text-primary" />
                      </div>
                    );
                  })()}
                  <div>
                    <span className="block">
                      {language === 'sw' ? fundDetail.fund.name_sw : fundDetail.fund.name}
                    </span>
                    <span className="text-sm font-normal text-muted-foreground">
                      {language === 'sw' ? fundDetail.fund.fund_type_sw : fundDetail.fund.fund_type}
                    </span>
                  </div>
                </DialogTitle>
                <DialogDescription>
                  {language === 'sw' ? fundDetail.fund.description_sw : fundDetail.fund.description_en}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6 mt-4">
                {/* Key Metrics */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-4 bg-muted/50 rounded-xl text-center">
                    <p className="text-xs text-muted-foreground mb-1">
                      {t('Unit Price', 'Bei')}
                    </p>
                    <p className="text-lg font-bold text-foreground">
                      TZS {fundDetail.fund.sale_price?.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-xl text-center">
                    <p className="text-xs text-muted-foreground mb-1">
                      {t('Annual Return', 'Faida/Mwaka')}
                    </p>
                    <p className="text-lg font-bold text-green-600">
                      {fundDetail.fund.annual_return}%
                    </p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-xl text-center">
                    <p className="text-xs text-muted-foreground mb-1">
                      {t('Min Investment', 'Kiwango cha chini')}
                    </p>
                    <p className="text-lg font-bold text-foreground">
                      TZS {formatNumber(fundDetail.fund.min_investment)}
                    </p>
                  </div>
                  <div className="p-4 bg-muted/50 rounded-xl text-center">
                    <p className="text-xs text-muted-foreground mb-1">
                      {t('Risk Level', 'Hatari')}
                    </p>
                    <Badge className={riskColors[fundDetail.fund.risk_level] || 'bg-gray-100'}>
                      {language === 'sw' ? fundDetail.fund.risk_level_sw : fundDetail.fund.risk_level}
                    </Badge>
                  </div>
                </div>
                
                {/* Performance */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground">
                    {t('Performance', 'Utendaji')}
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-muted-foreground">YTD</span>
                      <span className="font-medium text-green-600">
                        +{fundDetail.fund.ytd_return}%
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-muted-foreground">
                        {t('3 Years', 'Miaka 3')}
                      </span>
                      <span className="font-medium text-green-600">
                        {fundDetail.fund.three_year_return != null ? `+${fundDetail.fund.three_year_return}%` : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-muted-foreground">
                        {t('5 Years', 'Miaka 5')}
                      </span>
                      <span className="font-medium text-green-600">
                        {fundDetail.fund.five_year_return > 0
                          ? `+${fundDetail.fund.five_year_return}%`
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-muted-foreground">AUM</span>
                      <span className="font-medium text-foreground">
                        TZS {formatCurrency(fundDetail.fund.aum)}
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Features */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-foreground">
                    {t('Key Features', 'Sifa Muhimu')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(language === 'sw' 
                      ? fundDetail.fund.features_sw 
                      : fundDetail.fund.features_en
                    )?.map((feature, i) => (
                      <Badge key={i} variant="outline" className="py-1.5">
                        <CheckCircle2 className="w-3 h-3 mr-1 text-green-600" />
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                {/* Suitable For */}
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-xl">
                  <h4 className="font-medium text-foreground mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4 text-primary" />
                    {t('Best Suited For', 'Inafaa Kwa')}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {language === 'sw' 
                      ? fundDetail.fund.suitable_for_sw 
                      : fundDetail.fund.suitable_for_en}
                  </p>
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t border-border">
                  <Button 
                    className="flex-1"
                    onClick={() => {
                      setSimulatorFund(fundDetail.fund.id);
                      setActiveTab('simulator');
                      setIsDetailOpen(false);
                    }}
                  >
                    <Calculator className="w-4 h-4 mr-2" />
                    {t('Simulate Investment', 'Iga Uwekezaji')}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => window.open('https://uttamis.co.tz', '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('Visit UTT AMIS', 'Tembelea UTT AMIS')}
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
